/**
 * Renderer boot probe — WebGPU-first with WebGL2 fallback (ADR-005 §2).
 * The backend that actually initialized is REPORTED, never assumed (§9).
 */
import { PerspectiveCamera, Scene } from 'three';
import { WebGPURenderer } from 'three/webgpu';

export interface BootResult {
  backend: 'webgpu' | 'webgl2';
  renderer: WebGPURenderer;
}

export async function boot(canvas: HTMLCanvasElement): Promise<BootResult> {
  const renderer = new WebGPURenderer({ canvas, antialias: true });
  await renderer.init();

  // three's WebGPURenderer silently falls back to a WebGL2 backend when WebGPU
  // is unavailable — we surface which one actually initialized (fail-loud: a
  // deliberate fallback must be visible, never ambient). The runtime flag isn't
  // in the public types (r185), hence the structural cast.
  const backendFlags = renderer.backend as { isWebGPUBackend?: boolean };
  const backend: BootResult['backend'] = backendFlags.isWebGPUBackend === true ? 'webgpu' : 'webgl2';

  const scene = new Scene();
  const camera = new PerspectiveCamera(50, canvas.clientWidth / Math.max(1, canvas.clientHeight));
  camera.position.set(0, 1.6, 3);
  renderer.setSize(canvas.clientWidth, canvas.clientHeight, false);
  await renderer.render(scene, camera);

  return { backend, renderer };
}
